import contract from '../../lib/utm-observability-contract';

const { parseUtmParams: parseCanonicalUtmParams } = contract;

export const MARKETING_ATTRIBUTION_COOKIE_NAME = 'gf_marketing_attribution';
export const MARKETING_ATTRIBUTION_STORAGE_KEY = MARKETING_ATTRIBUTION_COOKIE_NAME;
export const MARKETING_ATTRIBUTION_COOKIE_DAYS = 90;
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];
export const MAX_ATTRIBUTION_VALUE_LENGTH = 255;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const getCookieExpiration = () =>
  new Date(Date.now() + MARKETING_ATTRIBUTION_COOKIE_DAYS * 24 * 60 * 60 * 1000).toUTCString();

export function getCookieDomain(hostname = isBrowser() ? window.location.hostname : '') {
  if (hostname === 'glowforge.com' || hostname.endsWith('.glowforge.com')) {
    return '.glowforge.com';
  }
  return null;
}

export function parseUtmParams(urlOrSearch) {
  const parsed = parseCanonicalUtmParams(
    urlOrSearch,
    isBrowser() ? window.location.href : 'https://glowforge.com/',
  );
  if (!parsed) return null;

  return UTM_KEYS.reduce((values, key) => {
    const value = Array.from(parsed[key] || '')
      .slice(0, MAX_ATTRIBUTION_VALUE_LENGTH)
      .join('');
    return value ? { ...values, [key]: value } : values;
  }, {});
}

function parseStoredAttribution(value) {
  if (!value) {
    return null;
  }

  try {
    const attribution = JSON.parse(value);
    if (attribution && typeof attribution === 'object') {
      return attribution;
    }
  } catch {
    // Ignore malformed attribution storage.
  }

  return null;
}

function readCookie() {
  if (!isBrowser()) {
    return null;
  }

  const cookie = document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(`${MARKETING_ATTRIBUTION_COOKIE_NAME}=`));

  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.slice(MARKETING_ATTRIBUTION_COOKIE_NAME.length + 1));
}

function readLocalStorage() {
  if (!isBrowser() || !window.localStorage) {
    return null;
  }

  try {
    return window.localStorage.getItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getMarketingAttributionStorageDiagnostics() {
  const cookieValue = readCookie();
  const localStorageValue = readLocalStorage();
  const cookieAttribution = parseStoredAttribution(cookieValue);
  const localStorageAttribution = parseStoredAttribution(localStorageValue);

  return {
    attribution_present: Boolean(cookieAttribution || localStorageAttribution),
    attribution_cookie_present: Boolean(cookieAttribution),
    attribution_local_storage_present: Boolean(localStorageAttribution),
    cookie_readback_matches: Boolean(
      cookieValue && localStorageValue && cookieValue === localStorageValue,
    ),
    cookie_payload_bytes: cookieValue ? new TextEncoder().encode(cookieValue).length : 0,
  };
}

function writeLocalStorage(value) {
  if (!isBrowser() || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(MARKETING_ATTRIBUTION_STORAGE_KEY, value);
  } catch {
    // Ignore unavailable storage.
  }
}

function writeCookie(value) {
  if (!isBrowser()) {
    return;
  }

  const cookieParts = [
    `${MARKETING_ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(value)}`,
    `expires=${getCookieExpiration()}`,
    'path=/',
    'SameSite=Lax',
  ];
  const domain = getCookieDomain();
  if (domain) {
    cookieParts.push(`domain=${domain}`);
  }
  if (window.location.protocol === 'https:') {
    cookieParts.push('Secure');
  }

  document.cookie = cookieParts.join('; ');
}

function persistAttribution(attribution) {
  const value = JSON.stringify(attribution);
  writeCookie(value);
  writeLocalStorage(value);
  return attribution;
}

export function getStoredMarketingAttribution() {
  return parseStoredAttribution(readCookie()) || parseStoredAttribution(readLocalStorage());
}

function normalizeFirstTouch(firstTouch) {
  if (!firstTouch || typeof firstTouch !== 'object') {
    return null;
  }

  const normalized = UTM_KEYS.reduce((values, key) => {
    const value = firstTouch[key];
    return typeof value === 'string' && value
      ? { ...values, [key]: value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH) }
      : values;
  }, {});

  if (typeof firstTouch.captured_at === 'string' && firstTouch.captured_at) {
    normalized.captured_at = firstTouch.captured_at;
  }

  return Object.keys(normalized).some((key) => key !== 'captured_at') ? normalized : null;
}

/**
 * Reads the first-touch attribution at the account-creation boundary.
 * A successful empty read is intentionally distinct from a failed read.
 */
export function getAccountCreationAttribution() {
  try {
    const firstTouch = normalizeFirstTouch(getStoredMarketingAttribution()?.first_touch);
    return {
      read: true,
      ...(firstTouch ? { first_touch: firstTouch } : {}),
    };
  } catch {
    // Attribution must never block account creation.
    return null;
  }
}

export function captureMarketingAttributionFromUrl(
  urlOrSearch = isBrowser() ? window.location.href : '',
) {
  if (!isBrowser()) {
    return null;
  }

  const utms = parseUtmParams(urlOrSearch);
  if (!utms) {
    return null;
  }

  const storedAttribution = getStoredMarketingAttribution() || {};
  const touch = {
    ...utms,
    captured_at: new Date().toISOString(),
  };
  return persistAttribution({
    ...storedAttribution,
    first_touch: storedAttribution.first_touch || touch,
    latest_touch: touch,
  });
}
